import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Button, Paper, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import useEstablecimientoPresentaciones from '../../Rar/hooks/useEstablecimientoPresentaciones';
import Loading from '../../../components/Loading';
import BackdropComponent from '../../../components/Backdrop/BackdropComponent';
import { Field } from '../../Presentaciones/components/DynamicData';
import { useAppDispatch, useAppSelector } from '../../../hooks/reduxHooks';
import { clearResponsables } from '../../../redux/slices/responsableSlice';
import { clearFields } from '../../../redux/slices/gremioContratistaSlice';
import useRGRLVerosimilitud from './hooks/useRGRLVerosimilitud';
import useRGRLVerosimilitudHandeler from './hooks/useRGRLVerosimilitudHandeler';
import VerosimilitudGrid from '../components/VerosimilitudGrid';
import ResponsableCargaSection from '../components/ResponsableCargaSection';
import { useGetVerosimilitudByIdQuery } from '../../../services/visitasApi';
import { skipToken } from '@reduxjs/toolkit/query';

type VerosimilitudMode = 'create' | 'edit';

interface VerosimilitudRouteState {
    mode: VerosimilitudMode;
    idVisita: number;
    idVerosimilitud?: number;
    selectedPresentationId: number;
    selectedEstablishmentId: number;
    tipoTitulo?: string;
}

const RGRLVerosimilitud: React.FC = () => {
    const dispatch = useAppDispatch();
    const location = useLocation();
    const { t } = useTranslation('visitas');
    const [loading, setLoading] = useState<boolean>(false);
    const {
        mode,
        idVisita,
        idVerosimilitud,
        selectedPresentationId,
        selectedEstablishmentId,
        tipoTitulo: initialTipoTitulo
    } = location.state as {
        mode: 'create' | 'edit';
        idVisita: number;
        idVerosimilitud?: number;
        selectedPresentationId: number;
        selectedEstablishmentId: number;
        tipoTitulo?: string;
    };

    const { data: verosimilitudData } = useGetVerosimilitudByIdQuery(
        mode === 'edit' && idVerosimilitud ? { idVisita, idVerosimilitud } : skipToken,
        {
            refetchOnMountOrArgChange: true
        }
    );

    const [tipoTitulo, setTipoTitulo] = useState<string>(location.state?.tipoTitulo);

    const [fieldOrganismo, setFieldOrganismo] = useState<Field[]>([]);
    const [respuestas, setRespuestas] = useState<any[]>([]);
    const [isInitialized, setIsInitialized] = useState(false);
    const { companyById } = useEstablecimientoPresentaciones(selectedEstablishmentId);
    const selectedVisita = location?.state?.selectedVisita;

    const { responsePresentacion, error, isLoading } = useRGRLVerosimilitud(selectedPresentationId);

    const fechaFinContrato = companyById?.organismo?.organismoCentral?.fechaFinContrato ?? null;

    const { requestSubmitVerosimilitud, isLoadingResponse } = useRGRLVerosimilitudHandeler({
        mode,
        idVisita,
        idVerosimilitud
    });

    const navigate = useNavigate();

    const handleCancel = () => {
        navigate(-1);
    };

    useEffect(() => {
        return () => {
            dispatch(clearResponsables());
            dispatch(clearFields());
        };
    }, [dispatch]);

    useEffect(() => {
        setTipoTitulo(responsePresentacion?.titulo || tipoTitulo);
    }, [responsePresentacion]);

    const mapBackendVerosimilitudToLocal = (
        respuestasBackend: {
            idPregunta?: number;
            idSeccion?: number;
            idTipoRespuesta?: number;
            idTipoRepuesta?: number;
            fechaRegularizacion?: string | null;
            fechaRegulacion?: string | null;
            fechaSeguimiento?: string | null;
            observacion?: string;
        }[],
        secciones: any[]
    ) => {
        const sectionById = new Map(secciones.map((seccion) => [Number(seccion.id), seccion]));
        const mappedByQuestionId = new Map<number, any>();

        const mapResponseToSelection = (r: any, idPregunta: number, idSeccion?: number) => {
            const idTipoRespuesta = Number(r.idTipoRespuesta ?? r.idTipoRepuesta ?? 3);

            return {
                idPregunta,
                idSeccion,
                opciones: [
                    { id: 1, selected: idTipoRespuesta === 1 },
                    { id: 2, selected: idTipoRespuesta === 2 },
                    { id: 3, selected: idTipoRespuesta === 3 }
                ],
                fechaRegularizacion: r.fechaRegularizacion ?? r.fechaRegulacion ?? null,
                fechaSeguimiento: r.fechaSeguimiento ?? null,
                observacion: r.observacion
            };
        };

        respuestasBackend.forEach((r: any) => {
            if (r.idPregunta) {
                mappedByQuestionId.set(
                    Number(r.idPregunta),
                    mapResponseToSelection(r, Number(r.idPregunta), r.idSeccion)
                );
                return;
            }

            const section = sectionById.get(Number(r.idSeccion));
            const sectionQuestions = Array.isArray(section?.preguntas) ? section.preguntas : [];

            if (sectionQuestions.length === 0 && r.idSeccion) {
                mappedByQuestionId.set(
                    Number(r.idSeccion),
                    mapResponseToSelection(r, Number(r.idSeccion), r.idSeccion)
                );
                return;
            }

            sectionQuestions.forEach((pregunta: any) => {
                mappedByQuestionId.set(
                    Number(pregunta.id),
                    mapResponseToSelection(r, Number(pregunta.id), r.idSeccion)
                );
            });
        });

        return Array.from(mappedByQuestionId.values());
    };

    useEffect(() => {
        if (mode === 'create' && responsePresentacion) {
            const secciones = responsePresentacion?.solapas?.[0]?.secciones || [];
            localStorage.removeItem('verosimilitud');
            localStorage.removeItem('verosimilitudFechaSeguimiento');
            if (secciones.length > 0) {
                const fromPresentacion = extractFromPresentacion(secciones);
                localStorage.setItem('verosimilitud', JSON.stringify(fromPresentacion));
            }
            setIsInitialized(true);
        }
    }, [mode, responsePresentacion]);

    useEffect(() => {
        if (companyById) {
            const establecimiento = companyById;
            const newFieldOrganismo: Field[] = [
                {
                    campo: t('headerRar.agencyName'),
                    valor: establecimiento?.organismo?.nombre || 'N/A'
                },
                {
                    campo: t('headerRar.agencyCuit'),
                    valor: establecimiento?.organismo?.cuit || 'N/A'
                },
                {
                    campo: t('headerRar.agencyContractNumber'),
                    valor: establecimiento?.organismo?.nroContrato || 'N/A'
                }
            ];
            setFieldOrganismo(newFieldOrganismo);
        }
    }, [companyById, t]);

    const extractFromPresentacion = (secciones: any[]) =>
        secciones.flatMap((seccion: any) =>
            (seccion.preguntas || []).map((pregunta: any) => {
                const selectedTipoRespuesta = (pregunta.tiposRespuesta || []).find(
                    (r: any) => r.selected
                );
                const desc = (selectedTipoRespuesta?.descripcion || '').trim().toUpperCase();
                const idTipoRespuesta = desc === 'SI' ? 1 : desc === 'NO' ? 2 : 3;
                return {
                    idPregunta: Number(pregunta.id),
                    idSeccion: Number(seccion.id),
                    opciones: [
                        { id: 1, selected: idTipoRespuesta === 1 },
                        { id: 2, selected: idTipoRespuesta === 2 },
                        { id: 3, selected: idTipoRespuesta === 3 }
                    ],
                    fechaRegularizacion: selectedTipoRespuesta?.fecha ?? null,
                    fechaSeguimiento: null,
                    observacion: undefined
                };
            })
        );

    useEffect(() => {
        const secciones = responsePresentacion?.solapas?.[0]?.secciones || [];
        if (mode === 'edit' && verosimilitudData?.data?.respuestas && secciones.length > 0) {
            localStorage.removeItem('verosimilitud');
            localStorage.removeItem('verosimilitudFechaSeguimiento');

            // Primary source: presentacion already has selected:true with correct opciones and fechaRegularizacion
            const fromPresentacion = extractFromPresentacion(secciones);

            // Overlay fechaSeguimiento and observacion from backend detail (not in presentacion)
            const fromBackend = mapBackendVerosimilitudToLocal(
                verosimilitudData.data.respuestas,
                secciones
            );
            const backendByPreguntaId = new Map(
                fromBackend.map((item: any) => [item.idPregunta, item])
            );

            const merged = fromPresentacion.map((item: any) => {
                const backendItem = backendByPreguntaId.get(item.idPregunta);
                if (!backendItem) return item;
                return {
                    ...item,
                    // Respuestas reales de verosimilitud (no las del RGRL de la presentación)
                    opciones: backendItem.opciones,
                    fechaRegularizacion:
                        backendItem.fechaRegularizacion ?? item.fechaRegularizacion,
                    fechaSeguimiento: backendItem.fechaSeguimiento ?? null,
                    observacion: backendItem.observacion ?? undefined
                };
            });

            localStorage.setItem('verosimilitud', JSON.stringify(merged));

            const fechaSeguimientoExistente = merged.find(
                (item: any) =>
                    item.opciones.some((op: any) => op.id === 2 && op.selected) &&
                    item.fechaSeguimiento
            )?.fechaSeguimiento;
            if (fechaSeguimientoExistente) {
                localStorage.setItem('verosimilitudFechaSeguimiento', fechaSeguimientoExistente);
            }

            setIsInitialized(true);
        }
    }, [mode, verosimilitudData, responsePresentacion]);

    const containerRef = useRef<HTMLDivElement>(null);

    if (isLoading) return <Loading />;
    if (isLoadingResponse) return <BackdropComponent loading={isLoadingResponse} />;

    return (
        <div>
            <Box sx={{ width: '100%' }}>
                {responsePresentacion && isInitialized && (
                    <>
                        <Box sx={{ mt: -9 }}>
                            <ResponsableCargaSection
                                responsables={responsePresentacion.responsables}
                            />
                        </Box>
                        <Box sx={{ pt: 2, pb: 10 }} ref={containerRef}>
                            <VerosimilitudGrid
                                mode={mode}
                                secciones={responsePresentacion?.solapas[0].secciones || []}
                                setRespuestas={setRespuestas}
                                respuestas={respuestas}
                                fechaFinContrato={fechaFinContrato}
                                tipoId={location.state?.tipoId}
                            />
                        </Box>

                        <Paper
                            elevation={0}
                            sx={{
                                position: 'fixed',
                                left: 0,
                                right: 0,
                                bottom: 0,
                                zIndex: (theme) => theme.zIndex.drawer - 1,
                                px: 3,
                                py: 1.5,
                                bgcolor: '#fff',
                                boxShadow: '0px -2px 8px rgba(0, 0, 0, 0.12)'
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 2,
                                    flexWrap: 'wrap',
                                    px: { xs: 2, md: '100px' }
                                }}
                            >
                                <Button
                                    onClick={handleCancel}
                                    variant="outlined"
                                    sx={{ minWidth: 170, height: 38 }}
                                >
                                    {t('verosimilitud.buttons.cancel')}
                                </Button>
                                <Button
                                    onClick={requestSubmitVerosimilitud}
                                    variant="contained"
                                    sx={{ minWidth: 170, height: 38 }}
                                >
                                    {t('verosimilitud.buttons.send')}
                                </Button>
                            </Box>
                        </Paper>
                    </>
                )}
            </Box>

            <BackdropComponent loading={loading} />
        </div>
    );
};

export default RGRLVerosimilitud;
